/**
 * Graficos SVG do Mercado de ALZ (sem lib de grafico: SVG responsivo via viewBox).
 *  - PriceChart: area + linha de preco com barras de volume embaixo e tooltip.
 *  - BarChart:   serie de barras (painel GM: faturamento / comissao por dia).
 */
import { useMemo, useState } from 'react';
import './rmt.css';

export interface ChartPoint {
  label: string;
  value: number;       // linha (preco) / altura da barra
  volume?: number;     // barras de baixo (PriceChart)
  hint?: string;       // texto extra no tooltip
}

const W = 640;
const H = 220;
const PAD = { l: 8, r: 8, t: 16, b: 22 };

export function PriceChart({
  points, format = (v: number) => v.toFixed(2), volumeFormat, height = 220, emptyText = 'Sem negociações no período',
}: {
  points: ChartPoint[];
  format?: (v: number) => string;
  volumeFormat?: (v: number) => string;
  height?: number;
  emptyText?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const has = points.some(p => p.value > 0);

  const geo = useMemo(() => {
    const vals = points.map(p => p.value).filter(v => v > 0);
    const min = vals.length ? Math.min(...vals) : 0;
    const max = vals.length ? Math.max(...vals) : 1;
    const span = max - min || max * 0.2 || 1;
    const lo = Math.max(0, min - span * 0.25);
    const hi = max + span * 0.25;
    const vmax = Math.max(1, ...points.map(p => p.volume || 0));
    const iw = W - PAD.l - PAD.r;
    const ih = H - PAD.t - PAD.b;
    const n = Math.max(1, points.length - 1);
    // Barra de volume e' centrada no ponto: recua a escala meia barra de cada lado,
    // senao a 1a e a ultima barra saem pra fora do container.
    const bw = Math.max(2, (iw / Math.max(1, points.length)) * 0.55);
    const xw = iw - bw;
    const x = (i: number) => PAD.l + bw / 2 + (points.length === 1 ? xw / 2 : (i / n) * xw);
    const y = (v: number) => PAD.t + ih - ((v - lo) / (hi - lo)) * ih * 0.78;
    // dias sem negocio herdam o ultimo preco (linha continua)
    let last = vals[0] ?? 0;
    const filled = points.map(p => { if (p.value > 0) last = p.value; return last; });
    const line = filled.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
    const area = `${line} L${x(points.length - 1).toFixed(1)},${PAD.t + ih} L${x(0).toFixed(1)},${PAD.t + ih} Z`;
    return { x, y, line, area, filled, vmax, ih, bw, lo, hi };
  }, [points]);

  if (!points.length || !has) {
    return (
      <div className="flex items-center justify-center text-white/35 text-sm" style={{ height }}>
        {emptyText}
      </div>
    );
  }

  const hp = hover !== null ? points[hover] : null;
  return (
    <div className="relative select-none" style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full overflow-hidden"
           onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id="rmtArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f0c86f" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#f0c86f" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="rmtLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#8fb9de" />
            <stop offset="100%" stopColor="#f0c86f" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map(f => (
          <line key={f} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + geo.ih * f} y2={PAD.t + geo.ih * f}
                stroke="rgba(255,255,255,0.05)" strokeDasharray="3 5" />
        ))}
        {points.map((p, i) => (p.volume ? (
          <rect key={`v${i}`} x={geo.x(i) - geo.bw / 2} width={geo.bw}
                y={PAD.t + geo.ih - (p.volume / geo.vmax) * geo.ih * 0.28}
                height={(p.volume / geo.vmax) * geo.ih * 0.28}
                fill={hover === i ? 'rgba(143,185,222,0.55)' : 'rgba(143,185,222,0.2)'} rx="1.5" />
        ) : null))}
        <path d={geo.area} fill="url(#rmtArea)" />
        <path d={geo.line} fill="none" stroke="url(#rmtLine)" strokeWidth="2.4" vectorEffect="non-scaling-stroke"
              strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null && (
          <>
            <line x1={geo.x(hover)} x2={geo.x(hover)} y1={PAD.t} y2={PAD.t + geo.ih}
                  stroke="rgba(240,200,111,0.45)" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
            <circle cx={geo.x(hover)} cy={geo.y(geo.filled[hover])} r="4.5" fill="#f0c86f"
                    stroke="#0a0f14" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          </>
        )}
        {points.map((_, i) => (
          <rect key={`h${i}`} x={geo.x(i) - W / points.length / 2} width={W / points.length} y={0} height={H}
                fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-white/35 px-1 pointer-events-none">
        <span>{points[0]?.label}</span>
        <span>{points[Math.floor(points.length / 2)]?.label}</span>
        <span>{points[points.length - 1]?.label}</span>
      </div>
      {hp && (
        <div className="absolute top-0 pointer-events-none px-2.5 py-1.5 rounded-lg bg-[#0a0f14]/95 border border-[#f0c86f]/30
                        text-[11px] text-white shadow-xl whitespace-nowrap"
             style={{ left: `${Math.min(80, Math.max(0, (geo.x(hover!) / W) * 100 - 8))}%` }}>
          <div className="text-white/50">{hp.label}</div>
          <div className="font-black rmt-gold">{hp.value > 0 ? format(hp.value) : 'sem negócio'}</div>
          {hp.volume ? <div className="text-[#8fb9de]">{volumeFormat ? volumeFormat(hp.volume) : hp.volume}</div> : null}
          {hp.hint && <div className="text-white/45">{hp.hint}</div>}
        </div>
      )}
    </div>
  );
}

export function BarChart({
  points, format = (v: number) => String(v), height = 180, color = '#f0c86f', secondary,
}: {
  points: ChartPoint[];
  format?: (v: number) => string;
  height?: number;
  color?: string;
  /** segunda serie sobreposta (ex.: comissao dentro do faturamento) */
  secondary?: { values: number[]; color: string; label: string };
}) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...points.map(p => p.value));
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;
  const slot = iw / Math.max(1, points.length);
  const bw = Math.max(3, slot * 0.62);
  const hp = hover !== null ? points[hover] : null;
  return (
    <div className="relative select-none" style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full" onMouseLeave={() => setHover(null)}>
        {[0.25, 0.5, 0.75].map(f => (
          <line key={f} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + ih * f} y2={PAD.t + ih * f}
                stroke="rgba(255,255,255,0.05)" strokeDasharray="3 5" />
        ))}
        {points.map((p, i) => {
          const h = (p.value / max) * ih;
          const x = PAD.l + i * slot + (slot - bw) / 2;
          const s = secondary ? (secondary.values[i] / max) * ih : 0;
          return (
            <g key={i} onMouseEnter={() => setHover(i)}>
              <rect x={PAD.l + i * slot} width={slot} y={0} height={H} fill="transparent" />
              <rect x={x} width={bw} y={PAD.t + ih - h} height={Math.max(h, p.value ? 1.5 : 0)} rx="2"
                    fill={color} opacity={hover === null || hover === i ? 0.85 : 0.35} />
              {secondary && s > 0 && (
                <rect x={x} width={bw} y={PAD.t + ih - s} height={s} rx="2" fill={secondary.color}
                      opacity={hover === null || hover === i ? 0.95 : 0.4} />
              )}
            </g>
          );
        })}
      </svg>
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-white/35 px-1 pointer-events-none">
        <span>{points[0]?.label}</span>
        <span>{points[points.length - 1]?.label}</span>
      </div>
      {hp && (
        <div className="absolute top-0 pointer-events-none px-2.5 py-1.5 rounded-lg bg-[#0a0f14]/95 border border-white/15
                        text-[11px] text-white shadow-xl whitespace-nowrap"
             style={{ left: `${Math.min(78, Math.max(0, ((hover! + 0.5) / points.length) * 100 - 8))}%` }}>
          <div className="text-white/50">{hp.label}</div>
          <div className="font-black" style={{ color }}>{format(hp.value)}</div>
          {secondary && <div style={{ color: secondary.color }}>{secondary.label}: {format(secondary.values[hover!])}</div>}
          {hp.hint && <div className="text-white/45">{hp.hint}</div>}
        </div>
      )}
    </div>
  );
}
